import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroImage from '../../assets/hero-image-1920.webp';
import heroImage1440 from '../../assets/hero-image-1440.webp';
import heroImage960 from '../../assets/hero-image-960.webp';
import { localizedPath, useCurrentLocale } from '../../lib/locale';
import { useRegions } from '../../lib/queries';
import type { Region } from '../../lib/types';
import { RegionOutlineMap } from '../regions/RegionOutlineMap';

/**
 * Home hero — spec §7.1, §25.5. The signature landing composition:
 *   overline → numbered 1·2·3 region STEPPER (active large/white, others dimmed, each →
 *   region page) + Plan Your Trip + play → outline map highlighting the active region →
 *   bottom-right "Learn More → /discover" card.
 *
 * The stepper and the map share one `activeIndex`: hovering/focusing a step (or the auto-
 * advance timer) sets it, and the map fills that region. Auto-advance is paused on pointer
 * interaction and disabled entirely under prefers-reduced-motion (§17 a11y).
 *
 * The background photo defaults to the bundled hero image; it becomes admin-curated later
 * (homepage settings, §8.4) via the `backgroundImage` prop. The dark gradient over it keeps
 * the white hero text AA-legible over any photo (§17).
 */

const ADVANCE_MS = 6000;

export function HomeHero({ backgroundImage = heroImage }: { backgroundImage?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useCurrentLocale();
  const { data: regions = [] } = useRegions();

  const steps = regions.slice(0, 3);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-advance the stepper, unless paused or the user prefers reduced motion.
  useEffect(() => {
    if (steps.length < 2 || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(
      () => setActiveIndex((i) => (i + 1) % steps.length),
      ADVANCE_MS,
    );
    return () => window.clearInterval(id);
  }, [steps.length, paused]);

  const activeRegion: Region | undefined = steps[activeIndex];
  const goToRegion = (slug: string) => navigate(localizedPath(`/what-to-see/${slug}`, locale));

  return (
    <section
      // Full-bleed (§25.2): edge to edge, starting under the transparent fixed header.
      className="relative isolate min-h-[88vh] w-full overflow-hidden bg-ink"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {backgroundImage && (
        /*
         * This is the LCP element of the busiest page (§17: LCP < 2.5s), so:
         *  - fetchPriority="high" — without it the browser discovers a background photo at
         *    normal priority and races it against the JS chunks it is already fetching.
         *  - NOT lazy. `loading="lazy"` on an above-the-fold LCP image delays the very paint
         *    being measured; lazy loading belongs on the carousels further down the page.
         *  - srcset only for the BUNDLED default. A custom `backgroundImage` from homepage
         *    settings (§8.4) is a single uploaded URL with no generated widths, and pairing it
         *    with this srcset would serve the bundled photo to anyone whose viewport matched a
         *    descriptor — i.e. silently ignore the curated image.
         */
        <img
          src={backgroundImage}
          srcSet={
            backgroundImage === heroImage
              ? `${heroImage960} 960w, ${heroImage1440} 1440w, ${heroImage} 1920w`
              : undefined
          }
          sizes="100vw"
          /*
           * Spread, and lowercase, on purpose. @types/react 18.3 declares the camelCase
           * `fetchPriority` prop but the React 18 RUNTIME does not implement it — so writing it
           * the typed way passes tsc and then warns on every single render. React 18 forwards an
           * all-lowercase unknown attribute to the DOM untouched, which is exactly what the
           * browser wants. Revisit when the app moves to React 19.
           */
          {...{ fetchpriority: 'high' }}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* Left-weighted so the stepper text stays legible while the map side reads lighter. */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/25" />

      {/* pt clears the 64px fixed header; content otherwise centres in the tall hero. */}
      <div className="relative mx-auto grid min-h-[88vh] max-w-7xl gap-8 px-4 pb-12 pt-24 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
        {/* Left: overline + stepper + CTAs */}
        <div>
          {/* Stable, semantic page heading (§17): the big hero text is a rotating stepper,
              so it can't be the h1. Visually hidden; screen readers and crawlers get one. */}
          <h1 className="sr-only">{t('home.heroTitle')}</h1>
          <p className="text-sm font-medium uppercase tracking-wide text-white/70">
            {t('home.overline')}
          </p>

          <ol className="mt-6 space-y-1">
            {steps.map((region, index) => {
              const active = index === activeIndex;
              return (
                <li key={region.id} className="flex items-center gap-4">
                  <span
                    aria-hidden="true"
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm transition-colors ${
                      active
                        ? 'border-white bg-white font-semibold text-ink'
                        : 'border-white/40 text-white/60'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onFocus={() => setActiveIndex(index)}
                    onClick={() => goToRegion(region.slug)}
                    aria-current={active}
                    className={`text-left font-bold uppercase leading-tight tracking-tight transition-all ${
                      active
                        ? 'text-3xl text-white sm:text-5xl'
                        : 'text-2xl text-white/35 hover:text-white/60 sm:text-4xl'
                    }`}
                  >
                    {region.name}
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={() => activeRegion && goToRegion(activeRegion.slug)}
              className="rounded-pill bg-accent px-6 py-3 font-medium text-accent-contrast transition-opacity hover:opacity-90"
            >
              {t('home.planYourTrip')}
            </button>
            <button
              type="button"
              aria-label={t('home.playVideo')}
              /*
                `text-primary` (the FIXED brand blue), not `text-accent`: this button sits on the
                hero photo and stays white in both themes, so a theme-variable ink turned into the
                dark theme's lightened accent on white — 2.82:1. The fixed brand gives 3.54:1,
                which clears the 3:1 WCAG 1.4.11 bar for a non-text glyph (the button is labelled
                for screen readers).
              */
              className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-primary transition-transform hover:scale-105"
            >
              <span aria-hidden="true">▶</span>
            </button>
          </div>
        </div>

        {/* Right: outline map highlighting the active region */}
        <div className="hidden lg:block">
          <RegionOutlineMap
            highlightRegion={activeRegion?.slug ?? null}
            onRegionClick={goToRegion}
            className="ml-auto h-[24rem] w-full max-w-xl"
          />
        </div>
      </div>

      {/* Bottom-right: Learn More → Discover hub (§20.1-17) */}
      <button
        type="button"
        onClick={() => navigate(localizedPath('/discover', locale))}
        className="absolute bottom-6 right-6 flex items-center gap-4 rounded-3xl bg-white/95 p-3 pr-5 text-left shadow-soft backdrop-blur transition-transform hover:scale-[1.02] dark:bg-white/90"
      >
        <span className="flex -space-x-3" aria-hidden="true">
          {['#34A853', '#F5A623', '#0EA5A5'].map((c) => (
            <span key={c} className="h-10 w-10 rounded-full border-2 border-white" style={{ background: c }} />
          ))}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-2 text-sm font-semibold text-ink">
            {t('hero.learnMore')} <span aria-hidden="true">→</span>
          </span>
          <span className="block text-xs text-ink/60">{t('home.discoverCardSubtitle')}</span>
        </span>
      </button>
    </section>
  );
}
